import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useDarDeBajaClaseDeSuelo } from "@/hooks/useClasesDeSuelo";
import { getApiErrorMessage } from "@/lib/api";
import type { ClaseDeSuelo } from "@/lib/suelos";

interface EliminarClaseDeSueloDialogProps {
  /** Clase a dar de baja; `null` cierra el diálogo. */
  clase: ClaseDeSuelo | null;
  onCerrar: () => void;
}

/**
 * Confirmación de baja de una clase del catálogo.
 *
 * El borrado es LÓGICO: los informes que ya la eligieron la siguen necesitando
 * para que su relación resuelva, y lo que cada uno IMPRIME no depende de esto
 * —cada fila del cuadro guarda su propia copia congelada del código—. Por eso
 * el aviso no promete que "se pierde", que sería falso: lo único que pasa es
 * que deja de ofrecerse.
 */
export function EliminarClaseDeSueloDialog({
  clase,
  onCerrar,
}: EliminarClaseDeSueloDialogProps) {
  const [error, setError] = useState<string | null>(null);

  /* Con el diálogo desmontado la alerta de adentro no la ve nadie: ahí el
     toast es la única señal posible. */
  const montado = useRef(true);
  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
    };
  }, []);

  const darDeBaja = useDarDeBajaClaseDeSuelo();

  if (!clase) return null;

  // `isPending` NO alcanza para bloquear el cierre: sin red, TanStack deja la
  // mutación PAUSADA con `isPending` en true sin haber enviado nada. Como el
  // diálogo es modal y atrapa el foco, condicionar las tres salidas solo a
  // `isPending` encerraría al usuario hasta recuperar conexión.
  const enVuelo = darDeBaja.isPending && !darDeBaja.isPaused;

  return (
    <AlertDialog
      open
      onOpenChange={(abierto) => {
        if (!abierto && !enVuelo) onCerrar();
      }}
    >
      <AlertDialogContent
        onEscapeKeyDown={(evento) => {
          if (enVuelo) evento.preventDefault();
        }}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>¿Dar de baja la clase «{clase.codigo}»?</AlertDialogTitle>
          <AlertDialogDescription>
            Deja de ofrecerse al describir los suelos de un informe. Los informes que ya
            la eligieron no cambian: cada uno guarda su propia copia del código. Si más
            adelante vuelves a crear una clase con el mismo código, se restaura esta
            misma.
          </AlertDialogDescription>
        </AlertDialogHeader>

        {error && (
          <div
            role="alert"
            className="bg-danger-soft text-danger-soft-foreground rounded-md px-3.5 py-3 text-sm"
          >
            {error}
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={enVuelo}>Cancelar</AlertDialogCancel>
          <Button
            variant="destructive"
            disabled={enVuelo}
            className="gap-2"
            onClick={() => {
              setError(null);
              darDeBaja.mutate(clase.id, {
                onSuccess: () => onCerrar(),
                onError: (fallo) => {
                  const mensaje = getApiErrorMessage(
                    fallo,
                    "No pudimos dar de baja la clase. Inténtalo de nuevo.",
                  );

                  if (montado.current) {
                    setError(mensaje);
                    return;
                  }

                  toast.error(mensaje);
                },
              });
            }}
          >
            {enVuelo && <Spinner />}
            Dar de baja
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
